import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { useUsers, ITEMS_PER_PAGE } from './hooks/useUsers';
import {
  UserFilters,
  UsersTable,
  UsersPagination,
  EditUserDialog,
  ViewUserDialog,
  DeleteUserDialog,
  RestoreUserDialog,
} from './components';
import { UsersPageProps } from './types';

export default function UsersPage({ isDark }: UsersPageProps) {
  const {
    users,
    total,
    totalPages,
    currentPage,
    isLoading,
    searchQuery,
    roleFilter,
    subscriptionFilter,
    deletionFilter,
    isEditDialogOpen,
    editFormData,
    viewingUser,
    isViewDialogOpen,
    isLoadingDetails,
    isSaving,
    deletingUser,
    restoringUser,
    isDeleting,
    isRestoring,
    setCurrentPage,
    handleSearch,
    handleRoleFilter,
    handleSubscriptionFilter,
    handleDeletionFilter,
    handleEdit,
    handleView,
    handleSave,
    handleDelete,
    handleRestore,
    setDeletingUser,
    setRestoringUser,
    setIsEditDialogOpen,
    setIsViewDialogOpen,
    setEditFormData,
  } = useUsers();

  const formatDate = (date: Date | string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className={cn(
          'text-2xl lg:text-3xl font-bold tracking-tight',
          isDark ? 'text-white' : 'text-[#111111]'
        )}>Пользователи</h1>
        <p className={cn(
          'text-sm mt-1',
          isDark ? 'text-slate-400' : 'text-slate-500'
        )}>Управление пользователями и их данными</p>
      </div>

      {/* Users Table Card */}
      <Card className={cn(
        'rounded-2xl border-0 overflow-hidden transition-all duration-300',
        isDark 
          ? 'bg-[#1A1A22] shadow-lg shadow-black/10' 
          : 'bg-white shadow-lg shadow-slate-200/50'
      )}>
        {/* Header with Filters */}
        <div className={cn(
          'p-5 lg:p-6 border-b',
          isDark ? 'border-slate-700' : 'border-slate-200'
        )}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h3 className={cn(
                'text-lg font-semibold',
                isDark ? 'text-white' : 'text-[#111111]'
              )}>Все пользователи</h3>
              <p className={cn(
                'text-sm mt-1',
                isDark ? 'text-slate-400' : 'text-slate-500'
              )}>
                {total} {deletionFilter === 'true' ? 'удалённых аккаунтов' : 'пользователей'} всего
              </p>
            </div>
            <UserFilters
              isDark={isDark}
              searchQuery={searchQuery}
              roleFilter={roleFilter}
              subscriptionFilter={subscriptionFilter}
              deletionFilter={deletionFilter}
              onSearch={handleSearch}
              onRoleFilter={handleRoleFilter}
              onSubscriptionFilter={handleSubscriptionFilter}
              onDeletionFilter={handleDeletionFilter}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <UsersTable
            isDark={isDark}
            users={users}
            isLoading={isLoading}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={setDeletingUser}
            onRestore={setRestoringUser}
            formatDate={formatDate}
          />
        </div>

        {/* Pagination */}
        {!isLoading && users.length > 0 && (
          <UsersPagination
            isDark={isDark}
            currentPage={currentPage}
            totalPages={totalPages}
            total={total}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        )}
      </Card>

      {/* Dialogs */}
      <EditUserDialog
        isDark={isDark}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={editFormData}
        onFormDataChange={setEditFormData}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <ViewUserDialog
        isDark={isDark}
        isOpen={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        user={viewingUser}
        isLoading={isLoadingDetails}
        formatDate={formatDate}
      />

      <DeleteUserDialog
        isDark={isDark}
        user={deletingUser}
        isDeleting={isDeleting}
        onOpenChange={() => setDeletingUser(null)}
        onConfirm={handleDelete}
      />

      <RestoreUserDialog
        isDark={isDark}
        user={restoringUser}
        isRestoring={isRestoring}
        onOpenChange={() => setRestoringUser(null)}
        onConfirm={handleRestore}
      />
    </div>
  );
}
